import { RefreshCw, Route } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { VisualPlanningBoard } from "../components/planning/VisualPlanningBoard";
import { useSyncChanges } from "../hooks/useSyncEngine";
import { extractErrorMessage, fetchPlanningBoard, savePlanningAssignment } from "../lib/api";

const getTomorrowDateInput = () => {
  const today = new Date();
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  const year = tomorrow.getFullYear();
  const month = `${tomorrow.getMonth() + 1}`.padStart(2, "0");
  const day = `${tomorrow.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const shiftDateInput = (value, daysToShift) => {
  const [year, month, day] = value.split("-").map(Number);
  const shifted = new Date(year, month - 1, day + daysToShift);
  return `${shifted.getFullYear()}-${`${shifted.getMonth() + 1}`.padStart(2, "0")}-${`${shifted.getDate()}`.padStart(2, "0")}`;
};

const buildVisualStripDays = () => {
  const today = new Date();
  const base = `${today.getFullYear()}-${`${today.getMonth() + 1}`.padStart(2, "0")}-${`${today.getDate()}`.padStart(2, "0")}`;
  return [
    { label: "Today", plan_date: base },
    { label: "Tomorrow", plan_date: shiftDateInput(base, 1) },
    { label: "Next day", plan_date: shiftDateInput(base, 2) },
  ];
};

const getSafetyMeta = (item, typeLabel) => {
  if (!item?.safety_expiration_date) {
    return `${typeLabel} safety missing`;
  }
  if (item.safety_expiration_date < new Date().toISOString().slice(0, 10)) {
    return `${typeLabel} expired ${item.safety_expiration_date}`;
  }
  return `${typeLabel} safety ${item.safety_expiration_date}`;
};

const buildDriverOptions = (drivers) => [
  { value: "", label: "No driver", searchText: "unassigned clear" },
  ...drivers.map((driver) => ({ value: driver.id, label: driver.full_name, searchText: `${driver.full_name} ${driver.phone || ""}` })),
];

const buildTruckOptions = (trucks) => [
  { value: "", label: "No truck", searchText: "unassigned clear" },
  ...trucks.map((truck) => ({ value: truck.id, label: truck.truck_number, meta: getSafetyMeta(truck, "Truck"), searchText: `${truck.truck_number} ${truck.safety_expiration_date || ""}` })),
];

const buildTrailerOptions = (trailers) => [
  { value: "", label: "No trailer", searchText: "unassigned clear" },
  ...trailers.map((trailer) => ({ value: trailer.id, label: trailer.trailer_number, meta: `${trailer.type || "Trailer"} • ${getSafetyMeta(trailer, "Trailer")}`, searchText: `${trailer.trailer_number} ${trailer.type || ""} ${trailer.safety_expiration_date || ""}` })),
];

export const PlanningBoardPage = () => {
  const [planDate, setPlanDate] = useState(getTomorrowDateInput());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingTripId, setSavingTripId] = useState("");
  const [viewMode, setViewMode] = useState("all");
  const [visiblePlanDate, setVisiblePlanDate] = useState(getTomorrowDateInput());
  const [visualDays, setVisualDays] = useState([]);
  const [lookups, setLookups] = useState({ drivers: [], trucks: [], trailers: [] });

  const loadPlanner = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const stripDays = buildVisualStripDays();
      const payloads = await Promise.all(stripDays.map((day) => fetchPlanningBoard({ plan_date: day.plan_date, search })));
      setVisualDays(stripDays.map((day, index) => ({ ...day, rows: payloads[index]?.rows || [] })));
      const selectedPayload = await fetchPlanningBoard({ plan_date: planDate, search });
      setLookups(selectedPayload.lookups || { drivers: [], trucks: [], trailers: [] });
      if (!stripDays.some((day) => day.plan_date === visiblePlanDate)) {
        setVisiblePlanDate(stripDays[0].plan_date);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to load the drag planner."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlanner();
  }, [planDate, search]);

  useSyncChanges(["planning", "load", "lookups"], () => loadPlanner({ silent: true }));

  const driverOptions = useMemo(() => buildDriverOptions(lookups.drivers || []), [lookups.drivers]);
  const truckOptions = useMemo(() => buildTruckOptions(lookups.trucks || []), [lookups.trucks]);
  const trailerOptions = useMemo(() => buildTrailerOptions(lookups.trailers || []), [lookups.trailers]);

  return (
    <div className="space-y-5" data-testid="planning-board-page">
      <section className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between" data-testid="planning-board-header">
        <div className="max-w-4xl space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Planning board</p>
          <h1 className="text-2xl font-semibold text-zinc-900" data-testid="planning-board-title">Drag and plan loads</h1>
          <p className="text-sm text-zinc-500" data-testid="planning-board-description">Unplanned loads and trips on the left, drop them into the right day column, and assign Driver, Truck, and Trailer inline in the same card.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm font-semibold text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900" data-testid="planning-board-open-dispatch-link" to="/dispatch-board">
            <Route className="h-4 w-4" /> Open Dispatch Board
          </Link>
        </div>
      </section>

      <section className="panel-surface grid gap-3 px-4 py-3 xl:grid-cols-[1fr_200px_220px_auto] xl:items-end" data-testid="planning-board-filter-panel">
        <label className="block space-y-1" data-testid="planning-board-search-wrap">
          <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Search unplanned loads or trips</span>
          <input className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900" data-testid="planning-board-search-input" onChange={(event) => setSearch(event.target.value)} placeholder="Search trip, load, customer, truck, trailer, location..." value={search} />
        </label>
        <label className="block space-y-1" data-testid="planning-board-date-wrap">
          <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Single-day focus</span>
          <input className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900" data-testid="planning-board-date-input" onChange={(event) => { setPlanDate(event.target.value); setVisiblePlanDate(event.target.value); }} type="date" value={planDate} />
        </label>
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2" data-testid="planning-board-date-summary">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500">Current view</p>
          <p className="mt-0.5 text-sm font-semibold text-zinc-900" data-testid="planning-board-date-summary-value">{viewMode === "all" ? "Today • Tomorrow • Next day" : visiblePlanDate}</p>
        </div>
        <button className="inline-flex items-center justify-center gap-2 rounded-xl bg-zinc-900 px-4 py-2 text-sm font-semibold text-white" data-testid="planning-board-refresh-button" onClick={loadPlanner} type="button">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </section>

      <VisualPlanningBoard
        assignmentSavingTripId={savingTripId}
        days={visualDays}
        driverOptions={driverOptions}
        onInvalidDrop={() => toast.error("Drop the card into its matching day column only.")}
        onSavePlan={async (payload) => {
          try {
            setSavingTripId(payload.trip_id);
            await savePlanningAssignment(payload);
            toast.success("Plan saved.");
            await loadPlanner();
          } catch (error) {
            toast.error(extractErrorMessage(error, "Unable to save this planned card."));
          } finally {
            setSavingTripId("");
          }
        }}
        trailerOptions={trailerOptions}
        truckOptions={truckOptions}
        viewMode={viewMode}
        visiblePlanDate={visiblePlanDate}
        onViewModeChange={setViewMode}
        onVisiblePlanDateChange={setVisiblePlanDate}
      />

      {loading ? <div className="panel-surface px-5 py-5 text-sm text-zinc-500" data-testid="planning-board-loading-state">Loading drag planner...</div> : null}
    </div>
  );
};

export default PlanningBoardPage;